import { FC, useEffect, useState } from 'react'
import {
	Box,
	Button,
	ButtonDropdown,
	ColumnLayout,
	Container,
	ContentLayout,
	FormField,
	Header,
	Input,
	SpaceBetween,
	StatusIndicator,
	Textarea
} from '@cloudscape-design/components'
import { useNavigate, useParams } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'

import ApproverOverview from '../../../../../components/ControlledFormInputs/ApproverOverview'
import useSingleMultiTenantRequest from '../../../../../hooks/query/useSingleMultiTenantRequest'
import useAcceptMultiTenantRequest from '../../../../../hooks/mutate/useAcceptMultiTenantRequest'
import useRejectMultiTenantRequest from '../../../../../hooks/mutate/useRejectMultiTenantRequest'
import LoadingSpinner from '../../../../../components/LoadingSpinner'
import type {
	TenantCidrAllocation
} from '../../../../../types/multiTenantRequest'

const cidrRegex =
	/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\/([0-9]|[1-2][0-9]|3[0-2])$/

const TenantDetails: FC = () => {
	const navigate = useNavigate()
	const { authState } = useOktaAuth()

	const currentUserEmail =
		(authState?.idToken?.claims?.email as string) ?? ''

	const { id: clusterId, tenantId } = useParams() as {
		id: string
		tenantId: string
	}

	const {
		data: tenant,
		isLoading,
		isError
	} = useSingleMultiTenantRequest(tenantId)

	const { mutate: acceptRequest, isLoading: isApproving } =
		useAcceptMultiTenantRequest(clusterId, tenantId)

	const { mutate: rejectRequest, isLoading: isRejecting } =
		useRejectMultiTenantRequest(clusterId, tenantId)

	const [selectedAction, setSelectedAction] = useState<
		'APPROVE' | 'REJECT' | null
	>(null)

	const [rejectionComment, setRejectionComment] = useState('')
	const [suggestedCorrection, setSuggestedCorrection] = useState('')
	const [validationErrors, setValidationErrors] = useState<
		Record<string, string>
	>({})

	const [systemAlias, setSystemAlias] = useState('')
	const [tenantProjectName, setTenantProjectName] = useState('')
	const [wbsCode, setWbsCode] = useState('')
	const [costCenter, setCostCenter] = useState('')
	const [cidrAllocations, setCidrAllocations] = useState<TenantCidrAllocation[]>([])

	// Populate local state once tenant data loads
	useEffect(() => {
		if (tenant) {
			setSystemAlias(tenant.details.systemAlias ?? '')
			setTenantProjectName(tenant.details.tenantProjectName ?? '')
			setWbsCode(tenant.details.wbsCode ?? '')
			setCostCenter(tenant.details.costCenter ?? '')
			setCidrAllocations(tenant.details.cidrAllocations ?? [])
		}
	}, [tenant])

	if (isLoading) return <LoadingSpinner />

	if (isError || !tenant) {
		return (
			<ContentLayout
				header={<Header variant="h1">Tenant not found</Header>}
			>
				<Button
					onClick={() => navigate(`/clusters/${clusterId}/multi-tenant`)}
				>
					Back to Tenants
				</Button>
			</ContentLayout>
		)
	}

	const isApproved = tenant.status === 'APPROVED'
	const isRejected = tenant.status === 'REJECTED'
	const isPending = tenant.status === 'PENDING_APPROVAL'
	const isProductionEnv = tenant.details.environment === 'pd'
	const isEditable = isPending || isRejected

	const hasPendingRequiredApprovals = (tenant.approvalStatus ?? []).some(
		(approval) => approval.status === null
	)

	const validateTenantDetails = () => {
		const errors: Record<string, string> = {}

		if (!systemAlias.trim()) {
			errors.systemAlias = 'Tenant Name is required.'
		}

		if (!tenantProjectName.trim()) {
			errors.tenantProjectName = 'Tenant Project Name is required.'
		}

		if (!wbsCode.trim()) {
			errors.wbsCode = 'WBS Code is required.'
		}

		if (!costCenter.trim()) {
			errors.costCenter = 'Cost Center is required.'
		}

		cidrAllocations.forEach((allocation) => {
			if (allocation.id === 'pods') return

			if (!allocation.purpose.trim()) {
				errors[`${allocation.id}-purpose`] = 'Purpose is required.'
			}

			if (!allocation.cidrAzA.trim()) {
				errors[`${allocation.id}-cidrAzA`] = 'CIDR AZ-A is required.'
			} else if (!cidrRegex.test(allocation.cidrAzA)) {
				errors[`${allocation.id}-cidrAzA`] =
					'Invalid CIDR format. Example: 100.64.0.0/16'
			}

			if (!allocation.cidrAzB.trim()) {
				errors[`${allocation.id}-cidrAzB`] = 'CIDR AZ-B is required.'
			} else if (!cidrRegex.test(allocation.cidrAzB)) {
				errors[`${allocation.id}-cidrAzB`] =
					'Invalid CIDR format. Example: 100.64.0.0/16'
			}

			if (isProductionEnv) {
				if (!allocation.cidrAzC?.trim()) {
					errors[`${allocation.id}-cidrAzC`] = 'CIDR AZ-C is required.'
				} else if (!cidrRegex.test(allocation.cidrAzC)) {
					errors[`${allocation.id}-cidrAzC`] =
						'Invalid CIDR format. Example: 100.64.0.0/16'
				}
			}
		})

		setValidationErrors(errors)
		return Object.keys(errors).length === 0
	}

	const handleApprove = () => {
		if (!validateTenantDetails()) return

		acceptRequest(
			{
				multiTenantRequestID: tenantId,
				approverID: currentUserEmail,
				action: 'APPROVE',
				details: {
					systemAlias,
					tenantName: systemAlias,
					tenantProjectName,
					wbsCode,
					costCenter,
					cidrAllocations
				}
			},
			{
				onSuccess: () => {
					navigate(`/clusters/${clusterId}/multi-tenant`)
				}
			}
		)
	}

	const handleReject = () => {
		if (!rejectionComment.trim()) {
			setValidationErrors((prev) => ({
				...prev,
				rejectionComment: 'Please enter a rejection reason.'
			}))
			return
		}

		if (!suggestedCorrection.trim()) {
			setValidationErrors((prev) => ({
				...prev,
				suggestedCorrection:
					'Please enter the suggested correction or expected value.'
			}))
			return
		}

		rejectRequest(
			{
				multiTenantRequestID: tenantId,
				commenterID: currentUserEmail,
				action: 'REJECT',
				comment: rejectionComment,
				suggestedCorrection
			},
			{
				onSuccess: () => {
					navigate(`/clusters/${clusterId}/multi-tenant`)
				}
			}
		)
	}

	const handleResubmit = () => {
		if (!validateTenantDetails()) return

		acceptRequest(
			{
				multiTenantRequestID: tenantId,
				approverID: currentUserEmail,
				action: 'APPROVE',
				details: {
					systemAlias,
					tenantName: systemAlias,
					tenantProjectName,
					wbsCode,
					costCenter,
					cidrAllocations
				}
			},
			{
				onSuccess: () => {
					navigate(`/clusters/${clusterId}/multi-tenant`)
				}
			}
		)
	}

	const handleCidrChange = (
		index: number,
		field: 'cidrAzA' | 'cidrAzB' | 'cidrAzC',
		value: string
	) => {
		setCidrAllocations((current) =>
			current.map((allocation, allocationIndex) =>
				allocationIndex === index
					? { ...allocation, [field]: value }
					: allocation
			)
		)
	}

	return (
		<ContentLayout
			header={
				<Header
					variant="h1"
					description="Review tenant onboarding request details."
				>
					{tenant.details.tenantName}
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				<Container>
					<SpaceBetween direction="vertical" size="l">
						<Box variant="h2">
							Status:{' '}
							{isApproved ? (
								<StatusIndicator type="success">Approved</StatusIndicator>
							) : isRejected ? (
								<StatusIndicator type="error">Rejected</StatusIndicator>
							) : (
								<StatusIndicator type="in-progress">
									Pending Approval
								</StatusIndicator>
							)}
						</Box>

						{isRejected && (
							<Container
								header={
									<Header variant="h3">Previous Rejection Details</Header>
								}
							>
								<SpaceBetween direction="vertical" size="s">
									<div>
										<Box variant="awsui-key-label">
											Reason for rejection
										</Box>
										<Box>
											{tenant.rejectionComment ??
												'No rejection comment provided.'}
										</Box>
									</div>

									<div>
										<Box variant="awsui-key-label">
											Suggested correction / expected value
										</Box>
										<Box>
											{tenant.suggestedCorrection ??
												'No suggested correction provided.'}
										</Box>
									</div>
								</SpaceBetween>
							</Container>
						)}

						<Header variant="h3">Multi-Tenant Onboarding Details</Header>

						<ColumnLayout columns={3}>
							<div>
								<Box variant="awsui-key-label">AWS Account ID</Box>
								<Input value={tenant.details.accountId} disabled />
							</div>

							<div>
								<Box variant="awsui-key-label">Region</Box>
								<Input value={tenant.details.region} disabled />
							</div>

							<div>
								<Box variant="awsui-key-label">Environment</Box>
								<Input value={tenant.details.environment} disabled />
							</div>

							<div>
								<Box variant="awsui-key-label">Workload Cluster Name</Box>
								<Input value={tenant.details.workloadCluster} disabled />
							</div>

							<div>
								<Box variant="awsui-key-label">Platform Name</Box>
								<Input value={tenant.details.platformName} disabled />
							</div>

							<div>
								<Box variant="awsui-key-label">Portfolio</Box>
								<Input value={tenant.details.portfolio} disabled />
							</div>

							<div>
								<Box variant="awsui-key-label">Requested By</Box>
								<Input value={tenant.requestorID} disabled />
							</div>

							<div>
								<Box variant="awsui-key-label">Created At</Box>
								<Input value={tenant.creationTime} disabled />
							</div>

							<div>
								<Box variant="awsui-key-label">Distribution List</Box>
								<Input value={tenant.details.distributionList} disabled />
							</div>
						</ColumnLayout>

						<Header variant="h3">Tenant Identity</Header>

						<ColumnLayout columns={3}>
							<FormField
								label="Tenant Name"
								errorText={validationErrors.systemAlias}
							>
								<Input
									value={systemAlias}
									disabled={!isEditable}
									onChange={({ detail }) =>
										setSystemAlias(detail.value.toUpperCase())
									}
								/>
							</FormField>

							<FormField
								label="Tenant Project Name"
								errorText={validationErrors.tenantProjectName}
							>
								<Input
									value={tenantProjectName}
									disabled={!isEditable}
									onChange={({ detail }) =>
										setTenantProjectName(detail.value.toUpperCase())
									}
								/>
							</FormField>

							<FormField
								label="WBS Code"
								errorText={validationErrors.wbsCode}
							>
								<Input
									value={wbsCode}
									disabled={!isEditable}
									onChange={({ detail }) =>
										setWbsCode(detail.value.toUpperCase())
									}
								/>
							</FormField>

							<FormField
								label="Cost Center"
								errorText={validationErrors.costCenter}
							>
								<Input
									value={costCenter}
									disabled={!isEditable}
									onChange={({ detail }) =>
										setCostCenter(detail.value.toUpperCase())
									}
								/>
							</FormField>

							<div>
								<Box variant="awsui-key-label">Data Classification</Box>
								<Input value={tenant.details.dataClassification} disabled />
							</div>

							<div>
								<Box variant="awsui-key-label">Funding Source Type</Box>
								<Input value={tenant.details.fundingSourceType} disabled />
							</div>
						</ColumnLayout>

						<Header variant="h3">CIDR Allocation / Subnet Tagging</Header>

						<SpaceBetween direction="vertical" size="m">
							{cidrAllocations.map(
								(allocation: TenantCidrAllocation, index: number) => (
									<ColumnLayout
										key={allocation.id}
										columns={isProductionEnv ? 4 : 3}
									>
										<FormField
											label="Purpose"
											errorText={
												validationErrors[`${allocation.id}-purpose`]
											}
										>
											<Input value={allocation.purpose} disabled />
										</FormField>

										<FormField
											label="CIDR AZ-A"
											errorText={
												validationErrors[`${allocation.id}-cidrAzA`]
											}
										>
											<Input
												value={allocation.cidrAzA}
												disabled={
													!isEditable || allocation.id === 'pods'
												}
												onChange={({ detail }) =>
													handleCidrChange(index, 'cidrAzA', detail.value)
												}
											/>
										</FormField>

										<FormField
											label="CIDR AZ-B"
											errorText={
												validationErrors[`${allocation.id}-cidrAzB`]
											}
										>
											<Input
												value={allocation.cidrAzB}
												disabled={
													!isEditable || allocation.id === 'pods'
												}
												onChange={({ detail }) =>
													handleCidrChange(index, 'cidrAzB', detail.value)
												}
											/>
										</FormField>

										{isProductionEnv && (
											<FormField
												label="CIDR AZ-C"
												errorText={
													validationErrors[`${allocation.id}-cidrAzC`]
												}
											>
												<Input
													value={allocation.cidrAzC ?? ''}
													disabled={
														!isEditable || allocation.id === 'pods'
													}
													onChange={({ detail }) =>
														handleCidrChange(
															index,
															'cidrAzC',
															detail.value
														)
													}
												/>
											</FormField>
										)}
									</ColumnLayout>
								)
							)}
						</SpaceBetween>

						<ApproverOverview
							isDisabled
							approvalStatus={tenant.approvalStatus ?? []}
						/>

						{isPending && (
							<Box float="right">
								<ButtonDropdown
									items={[
										{ id: 'approve', text: 'To Approve...' },
										{ id: 'reject', text: 'To Reject...' }
									]}
									onItemClick={({ detail }) => {
										if (detail.id === 'approve') {
											setSelectedAction('APPROVE')
										}
										if (detail.id === 'reject') {
											setSelectedAction('REJECT')
										}
									}}
								>
									Action
								</ButtonDropdown>
							</Box>
						)}

						{isRejected && (
							<Box float="right">
								<Button
									variant="primary"
									onClick={handleResubmit}
									loading={isApproving}
								>
									Resubmit Request
								</Button>
							</Box>
						)}
					</SpaceBetween>
				</Container>

				{selectedAction === 'APPROVE' && (
					<Container
						header={<Header variant="h3">Tenant Approval Form</Header>}
					>
						<SpaceBetween direction="vertical" size="l">
							<Box>Ready to approve this tenant onboarding request.</Box>

							{hasPendingRequiredApprovals && (
								<Box color="text-status-warning">
									Required team approvals are still pending. Final approval
									is currently blocked.
								</Box>
							)}

							<Box float="right">
								<SpaceBetween direction="horizontal" size="xs">
									<Button
										variant="link"
										onClick={() => setSelectedAction(null)}
									>
										Cancel
									</Button>

									<Button
										variant="primary"
										onClick={handleApprove}
										loading={isApproving}
										disabled={hasPendingRequiredApprovals}
									>
										Approve
									</Button>
								</SpaceBetween>
							</Box>
						</SpaceBetween>
					</Container>
				)}

				{selectedAction === 'REJECT' && (
					<Container
						header={<Header variant="h3">Reject Tenant Request</Header>}
					>
						<SpaceBetween direction="vertical" size="l">
							<FormField
								label="Reason for rejection"
								errorText={validationErrors.rejectionComment}
							>
								<Textarea
									value={rejectionComment}
									onChange={({ detail }) => {
										setRejectionComment(detail.value)
										setValidationErrors((prev) => ({
											...prev,
											rejectionComment: ''
										}))
									}}
									placeholder="Enter rejection reason"
								/>
							</FormField>

							<FormField
								label="Suggested correction / expected value"
								errorText={validationErrors.suggestedCorrection}
							>
								<Textarea
									value={suggestedCorrection}
									onChange={({ detail }) => {
										setSuggestedCorrection(detail.value)
										setValidationErrors((prev) => ({
											...prev,
											suggestedCorrection: ''
										}))
									}}
									placeholder="Enter the correct value or instruction for the requestor"
								/>
							</FormField>

							<Box float="right">
								<SpaceBetween direction="horizontal" size="xs">
									<Button
										variant="link"
										onClick={() => setSelectedAction(null)}
									>
										Cancel
									</Button>

									<Button
										variant="primary"
										onClick={handleReject}
										loading={isRejecting}
									>
										Reject
									</Button>
								</SpaceBetween>
							</Box>
						</SpaceBetween>
					</Container>
				)}

				<Box float="right">
					<Button
						variant="link"
						onClick={() => navigate(`/clusters/${clusterId}/multi-tenant`)}
					>
						Back to Tenants
					</Button>
				</Box>
			</SpaceBetween>
		</ContentLayout>
	)
}

export default TenantDetails
